import * as XLSX from 'xlsx';

const REQUIRED_COLUMNS = ['date', 'description', 'time'];
const MAX_IMPORT_ROWS = 2000;
const MAX_IMPORT_FILE_BYTES = 10 * 1024 * 1024;
const HEADER_ALIASES = {
    date: new Set(['date', 'entry date']),
    description: new Set(['description', 'narrative', 'details']),
    time: new Set(['time', 'hours', 'duration']),
    user: new Set(['user', 'user id', 'userid', 'team member', 'timekeeper']),
};

const normalizeHeader = (value) => String(value ?? '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

const canonicalHeader = (value) => {
    const normalized = normalizeHeader(value);
    return Object.entries(HEADER_ALIASES).find(([, aliases]) => aliases.has(normalized))?.[0] || null;
};

const isBlankCell = (value) => value === null
    || value === undefined
    || (typeof value === 'string' && value.trim() === '');

const formatCalendarDate = (year, month, day) => {
    const numericYear = Number(year);
    const numericMonth = Number(month);
    const numericDay = Number(day);
    const check = new Date(numericYear, numericMonth - 1, numericDay);
    if (!Number.isInteger(numericYear)
        || !Number.isInteger(numericMonth)
        || !Number.isInteger(numericDay)
        || numericYear < 1970
        || numericYear > 2038
        || check.getFullYear() !== numericYear
        || check.getMonth() !== numericMonth - 1
        || check.getDate() !== numericDay) {
        return null;
    }
    return `${String(numericYear).padStart(4, '0')}-${String(numericMonth).padStart(2, '0')}-${String(numericDay).padStart(2, '0')}`;
};

export const normalizeBillingTimeDateCell = (value) => {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return formatCalendarDate(value.getFullYear(), value.getMonth() + 1, value.getDate()) || '';
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        const parsed = XLSX.SSF.parse_date_code(value);
        if (parsed) return formatCalendarDate(parsed.y, parsed.m, parsed.d) || String(value);
    }

    const raw = String(value ?? '').trim();
    if (!raw) return '';

    const iso = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T].*)?$/);
    if (iso) return formatCalendarDate(iso[1], iso[2], iso[3]) || raw;

    const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
    if (us) {
        let year = Number(us[3]);
        if (us[3].length === 2) year += year >= 70 ? 1900 : 2000;
        return formatCalendarDate(year, us[1], us[2]) || raw;
    }

    return raw;
};

const findImportSheet = (workbook) => {
    for (const sheetName of workbook?.SheetNames || []) {
        const sheet = workbook.Sheets?.[sheetName];
        if (!sheet) continue;
        const rows = XLSX.utils.sheet_to_json(sheet, {
            header: 1,
            raw: true,
            defval: '',
            blankrows: false,
        });
        const headerIndex = rows.findIndex((row) => Array.isArray(row) && row.some((cell) => !isBlankCell(cell)));
        if (headerIndex !== -1) return { sheetName, rows, headerIndex };
    }
    return null;
};

export const parseBillingTimeWorkbook = (workbook) => {
    const selected = findImportSheet(workbook);
    if (!selected) throw new Error('The spreadsheet does not contain any rows.');

    const headerRow = selected.rows[selected.headerIndex];
    const columns = {};
    headerRow.forEach((header, index) => {
        const canonical = canonicalHeader(header);
        if (!canonical) return;
        if (columns[canonical] !== undefined) {
            throw new Error(`The spreadsheet contains more than one ${canonical} column.`);
        }
        columns[canonical] = index;
    });

    const missing = REQUIRED_COLUMNS.filter((column) => columns[column] === undefined);
    if (missing.length > 0) {
        throw new Error(`Missing required column${missing.length === 1 ? '' : 's'}: ${missing.join(', ')}.`);
    }

    const rows = [];
    selected.rows.slice(selected.headerIndex + 1).forEach((row, relativeIndex) => {
        if (!Array.isArray(row) || row.every(isBlankCell)) return;
        const userValue = columns.user === undefined ? '' : row[columns.user];
        rows.push({
            source_row: selected.headerIndex + relativeIndex + 2,
            date: normalizeBillingTimeDateCell(row[columns.date]),
            description: String(row[columns.description] ?? '').trim(),
            time: row[columns.time] ?? '',
            user: typeof userValue === 'string' ? userValue.trim() : userValue,
        });
    });

    if (rows.length === 0) throw new Error('The spreadsheet does not contain any time-entry rows.');
    if (rows.length > MAX_IMPORT_ROWS) throw new Error(`Import no more than ${MAX_IMPORT_ROWS.toLocaleString()} rows at a time.`);
    return { sheetName: selected.sheetName, rows };
};

export const parseBillingTimeFile = async (file) => {
    if (!file) throw new Error('Choose an Excel or CSV file.');
    const extension = String(file.name || '').toLowerCase().match(/\.[^.]+$/)?.[0] || '';
    if (!['.csv', '.xls', '.xlsx'].includes(extension)) {
        throw new Error('Choose a .csv, .xls, or .xlsx file.');
    }
    if (Number(file.size || 0) > MAX_IMPORT_FILE_BYTES) {
        throw new Error('Choose a spreadsheet no larger than 10 MB.');
    }
    const data = await file.arrayBuffer();
    let sourceSha256 = '';
    const cryptoApi = typeof window === 'undefined' ? null : window.crypto;
    if (cryptoApi?.subtle) {
        try {
            const digest = await cryptoApi.subtle.digest('SHA-256', data);
            sourceSha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
        } catch (error) {
            sourceSha256 = '';
        }
    }
    const workbook = XLSX.read(data, { type: 'array', cellDates: true });
    return { ...parseBillingTimeWorkbook(workbook), sourceSha256 };
};

export const createBillingTimeImportRequestId = () => {
    const cryptoApi = typeof window === 'undefined' ? null : window.crypto;
    if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
    const randomByte = () => {
        if (cryptoApi?.getRandomValues) {
            const value = new Uint8Array(1);
            cryptoApi.getRandomValues(value);
            return value[0];
        }
        return Math.floor(Math.random() * 256);
    };
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
        const value = randomByte();
        const nibble = character === 'x' ? value & 0xf : (value & 0x3) | 0x8;
        return nibble.toString(16);
    });
};

export const downloadBillingTimeTemplate = () => {
    const contents = [
        'date,description,time,user',
        '3/3/26,"Receive and review correspondence.",0.1,',
        '3/4/26,"Team meeting and case strategy.",2,',
    ].join('\r\n');
    const url = URL.createObjectURL(new Blob([contents], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'CaseDB Time Import Template.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};
